import React from 'react';
import { graphql, fetchQuery } from 'relay-runtime';
import type { IEnvironment } from 'relay-runtime';
import type { FireUIEventPayload } from '../../analytics/useForgeUiAnalyticsEvent';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';
import { egressPermissionService } from '../../utils/useEgressPermissionService';
import { getLinkType, getAnalyticsAttributes } from '../../custom-ui/bridge/methods/useNavigation';
import { makeUrl } from '../../custom-ui/bridge/methods/makeUrl';
import { navigate as navigateToUrl } from '../../custom-ui/bridge/methods/navigate';
import { isCustomDomain } from '../../custom-ui/bridge/utils/isCustomDomain';
import { createNavigateTargetPath } from '../../custom-ui/bridge/utils/createNavigateTargetPath';
import { getForgeUIExtensionsRelayAsync } from '../../provider/getForgeUIExtensionsAsync';
import { GQLExtensionContextsFilterType } from '../../web-client/graphql/types';
import { BridgeClientError } from '../../custom-ui/bridge';
import { ExternalLinkModal } from '../../components/externalLinkModal';
import type { NavigatePayload } from '../../custom-ui/bridge';
import type { TargetURLUtils } from '../../custom-ui/bridge/utils/navigation-targets/common';
import type { OverlayController } from '../types';
import type { Extension } from '../../web-client';

// ─── Types ───────────────────────────────────────────────────────────────────

type NavigateType = 'same-tab' | 'new-tab';

type TargetNavigatePayload = {
	type: NavigateType;
} & Parameters<typeof createNavigateTargetPath>[0];

// ─── Module-level base URL cache ──────────────────────────────────────────────

const storedBaseUrlRelayByCloudId = new Map<string, string>();

/** Exported for testing purposes only. */
export const resetStoredBaseUrlRelay = (): void => {
	storedBaseUrlRelayByCloudId.clear();
};

/**
 * Retrieves the base URL for the given cloud tenant using a Relay query,
 * caching the result for the lifetime of the module. Needed to support
 * cross-custom-domain routing when `isCustomDomain()` returns true.
 */
const getBaseUrl = (
	relayEnvironment: IEnvironment,
	cloudId: string | undefined,
): Promise<string> => {
	if (!cloudId) {
		return Promise.resolve(window.location.origin);
	}

	const cachedBaseUrl = storedBaseUrlRelayByCloudId.get(cloudId);
	if (cachedBaseUrl) {
		return Promise.resolve(cachedBaseUrl);
	}

	const observable = fetchQuery(
		relayEnvironment,
		graphql`
			query navigationBridgeMethod_tenantUrlQuery($cloudIds: [ID!]) {
				tenantContexts(cloudIds: $cloudIds) {
					cloudUrl
				}
			}
		`,
		{ cloudIds: [cloudId] },
	);

	return new Promise<string>((resolve, reject) => {
		let settled = false;
		observable.subscribe({
			next: (data) => {
				if (settled) {
					return;
				}
				settled = true;
				const baseUrl =
					(data as { tenantContexts?: Array<{ cloudUrl: string }> } | null)?.tenantContexts?.[0]
						?.cloudUrl ?? '';
				storedBaseUrlRelayByCloudId.set(cloudId, baseUrl);
				resolve(baseUrl);
			},
			error: (err: unknown) => {
				if (!settled) {
					settled = true;
					reject(err);
				}
			},
			complete: () => {
				if (!settled) {
					settled = true;
					resolve(storedBaseUrlRelayByCloudId.get(cloudId) ?? '');
				}
			},
		});
	});
};

// ─── Extension query helper ───────────────────────────────────────────────────

/**
 * Returns a `getAppForgeUIExtensions` function compatible with `TargetURLUtils`,
 * pre-bound to the current extension's appId + environmentId. Uses Relay directly
 * (no Apollo fallback) since the bridge layer is Relay-only.
 */
const makeGetAppForgeUIExtensions = (
	relayEnvironment: IEnvironment,
	contextIds: string[],
	appId: string,
	environmentId: string,
	cloudId: string | undefined,
): TargetURLUtils['getAppForgeUIExtensions'] => {
	return (params) =>
		getForgeUIExtensionsRelayAsync({
			contextIds,
			extensionsFilter: [
				{
					type: GQLExtensionContextsFilterType.AppIdEnvironmentId,
					value: [`${appId}:${environmentId}`],
				},
			],
			cloudId,
			relayEnvironment,
			...params,
			// Must use the product-agnostic extensionContexts query (bypassNewForgeExtensionListCall: true).
			// Without this flag, getForgeUIExtensionsRelayAsync defaults to the confluence_forgeExtensionsByType
			// query which returns no results outside of Confluence, causing module key lookups to silently
			// fail in Jira and other products.
			bypassNewForgeExtensionListCall: true,
		});
};

// ─── Shared onNavigate helper ─────────────────────────────────────────────────

/**
 * Validates the URL, navigates immediately for trusted links, and for external
 * links shows the ExternalLinkModal via overlayController before navigating.
 */
const onNavigate = async (
	{ type, url: urlString }: NavigatePayload,
	{
		extension,
		overlayController,
		analytics: { fireUIEvent },
	}: {
		analytics: { fireUIEvent: (payload: FireUIEventPayload) => void };
		extension: Pick<Extension, 'egress' | 'properties'>;
		overlayController: OverlayController;
	},
): Promise<void> => {
	const url = makeUrl(urlString);
	const egressService = egressPermissionService('FETCH_CLIENT_SIDE', extension.egress);
	const linkType = getLinkType(url, egressService);

	if (linkType === 'invalid' || !url) {
		throw new BridgeClientError(`link is invalid: ${urlString}`);
	}

	if (linkType === 'trusted') {
		navigateToUrl(url.href, type);
		return;
	}

	const analyticsAttributes = getAnalyticsAttributes(url);

	fireUIEvent({
		action: 'viewed',
		actionSubject: 'externalLinkModal',
		attributes: analyticsAttributes,
	});

	return new Promise<void>((resolve, reject) => {
		overlayController.show(
			<ExternalLinkModal
				url={url}
				extensionTitle={extension.properties?.title as string | undefined}
				onConfirm={() => {
					navigateToUrl(url.href, type);
					fireUIEvent({
						action: 'approved',
						actionSubject: 'externalLinkModal',
						attributes: analyticsAttributes,
					});
					overlayController.hide();
					resolve();
				}}
				onCancel={() => {
					fireUIEvent({
						action: 'cancelled',
						actionSubject: 'externalLinkModal',
						attributes: analyticsAttributes,
					});
					overlayController.hide();
					reject(new BridgeClientError('cancelled'));
				}}
			/>,
		);
	});
};

// ─── getUrl ───────────────────────────────────────────────────────────────────

type GetUrlContext = MakeBridgeContext<
	'extension' | 'extensionDetails' | 'contextIds' | 'viewContext',
	'relayEnvironment'
>;

/**
 * Returns the fully-qualified URL for a given navigation target, or `null` if
 * the target cannot be resolved. Does not navigate; useful for link generation.
 *
 * This is a stateless port of the legacy `getUrl` handler in `useBridge.tsx`.
 */
export const getUrl: TBridgeMethod<GetUrlContext, TargetNavigatePayload, string | null> = async (
	{ extension, extensionDetails, contextIds, viewContext, services: { relayEnvironment } },
	data,
) => {
	if (!data.target) {
		return null;
	}

	const { appId } = extensionDetails;
	const { environmentId } = extension;

	const path = await createNavigateTargetPath(data, {
		getContext: async () => viewContext,
		getAppForgeUIExtensions: makeGetAppForgeUIExtensions(
			relayEnvironment,
			contextIds,
			appId,
			environmentId,
			viewContext.cloudId,
		),
	});

	if (!path) {
		return null;
	}

	const baseUrl = isCustomDomain(window.location.hostname)
		? await getBaseUrl(relayEnvironment, viewContext.cloudId)
		: window.location.origin;

	return `${baseUrl}${path}`;
};

// ─── navigate ─────────────────────────────────────────────────────────────────

type NavigateContext = MakeBridgeContext<
	'extension' | 'extensionDetails' | 'contextIds' | 'viewContext',
	'relayEnvironment' | 'overlayController' | 'analytics'
>;

/**
 * Navigates to a URL or a typed navigation target.
 *
 * - For direct URL payloads (`{ type, url }`): validates via egress/trusted-domain
 *   checks and either navigates immediately (trusted) or shows the ExternalLinkModal
 *   (external), mirroring the legacy `useNavigation().onNavigate()` behaviour.
 * - For typed target payloads (`{ target, type, ... }`): resolves the path via
 *   `createNavigateTargetPath`, prepends the tenant base URL when on a custom domain,
 *   then delegates to the URL-based path above.
 *
 * This is a stateless port of the legacy `navigate` handler in `useBridge.tsx`.
 */
export const navigate: TBridgeMethod<
	NavigateContext,
	NavigatePayload | TargetNavigatePayload,
	void
> = async (
	{
		extension,
		extensionDetails,
		contextIds,
		viewContext,
		services: { relayEnvironment, overlayController, analytics },
	},
	data,
) => {
	if ('target' in data && data.target) {
		const targetData = data as TargetNavigatePayload;
		const { appId } = extensionDetails;
		const { environmentId } = extension;

		const path = await createNavigateTargetPath(targetData, {
			getContext: async () => viewContext,
			getAppForgeUIExtensions: makeGetAppForgeUIExtensions(
				relayEnvironment,
				contextIds,
				appId,
				environmentId,
				viewContext.cloudId,
			),
		});

		if (!path) {
			// eslint-disable-next-line no-console
			console.warn('Path was not found for the parameters provided');
			return;
		}

		const url = isCustomDomain(window.location.hostname)
			? `${await getBaseUrl(relayEnvironment, viewContext.cloudId)}${path}`
			: path;

		return onNavigate({ type: targetData.type, url }, { extension, overlayController, analytics });
	}

	return onNavigate(data as NavigatePayload, { extension, overlayController, analytics });
};
